import api from './api';

export interface StaffMember {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager';
  createdAt: string;
  updatedAt: string;
  failedAttempts?: number;
  lockedUntil?: string | null;
  locationId?: string | null;
  location?: { id: string; name: string } | null;
}

export interface StaffListResponse {
  data: StaffMember[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateStaffRequest {
  email: string;
  name: string;
  role: 'admin' | 'manager';
  password: string;
  locationId?: string | null;
}

export interface UpdateStaffRequest {
  name?: string;
  role?: 'admin' | 'manager';
  password?: string;
  locationId?: string | null;
}

export interface StaffFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'admin' | 'manager';
}

export const staffService = {
  // Get all staff members
  async getStaffMembers(filters?: StaffFilters): Promise<StaffListResponse> {
    const response = await api.get('/staff', { params: filters });
    return response.data;
  },

  // Get single staff member
  async getStaffMember(id: string): Promise<StaffMember> {
    const response = await api.get(`/staff/${id}`);
    return response.data.data;
  },

  // Create new staff member
  async createStaffMember(data: CreateStaffRequest): Promise<StaffMember> {
    const response = await api.post('/staff', data);
    return response.data.data;
  },

  // Update staff member
  async updateStaffMember(id: string, data: UpdateStaffRequest): Promise<StaffMember> {
    const response = await api.put(`/staff/${id}`, data);
    return response.data.data;
  },

  // Delete staff member
  async deleteStaffMember(id: string): Promise<void> {
    await api.delete(`/staff/${id}`);
  },

  // Unlock staff account
  async unlockStaffAccount(id: string): Promise<void> {
    await api.post(`/staff/${id}/unlock`);
  },

  // Get current user's profile
  async getProfile(): Promise<StaffMember> {
    const response = await api.get('/staff/profile');
    return response.data.data;
  },

  // Update current user's profile (name only)
  async updateProfile(name: string): Promise<StaffMember> {
    const response = await api.put('/staff/profile', { name });
    return response.data.data;
  },

  // Change current user's password
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.put('/staff/profile/password', {
      currentPassword,
      newPassword
    });
    return response.data.data;
  }
};